import {
  IServerSideGetRowsParams,
  RowClickedEvent,
  RowNode,
} from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga';

import TableGrid from '../../../../../components/TableGrid';
import TablePagination from '../../../../../components/TableGrid/Pagination';
import { refreshTableFn } from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContexConstructor';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { FontSizes } from '../../../../../core/config/mainTheme';
import { PageOfQueryDto, QueryDto } from '../../../../../generated/model/models';
import { useCompaniesDispatch } from '../../../shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../../shared/CompanyContext/CompanyContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import {
  FinancialDataContextActionTypes,
  RequestParams,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import { getQueriesPanelTableColumns } from './QueriesPanelTableColumns';

interface Props {
  datasource: (params: any) => Promise<PageOfQueryDto>;
  className?: any;
  refresh?: boolean;
  isPredefinedFilters?: boolean;
}

export const QueriesPanelTable: React.FC<Props> = ({
  datasource,
  className,
  refresh,
  isPredefinedFilters = false,
}) => {
  const classes = useStyles();
  const dispatch = useFilterDispatch();
  const dispatchCompanies = useCompaniesDispatch();
  const columns = getQueriesPanelTableColumns();
  const { tableGridApi } = useTableGridApiState();
  const dispatchTableApi = useTableGridApiDispatch();
  const { predefinedFilters, appliedFilters } = useFilterState();
  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    totalPages: 0,
    totalElements: 0,
  });

  useEffect(() => {
    if (
      isPredefinedFilters &&
      appliedFilters.length === 1 &&
      appliedFilters.indexOf('activityKeywords') !== -1 &&
      predefinedFilters &&
      tableGridApi
    ) {
      tableGridApi.forEachNode((rowNode: RowNode) => {
        if (rowNode.rowIndex === 0) {
          rowNode.setSelected(true);
        }
      });
    }
  }, [tableGridApi, appliedFilters, predefinedFilters, isPredefinedFilters]);

  const onRowClicked = (event: RowClickedEvent) => {
    const searchRequest = (event?.data as QueryDto)?.searchRequest;

    if (searchRequest) {
      ReactGA.event({
        category: 'SEARCH',
        action: isPredefinedFilters ? 'POPULAR_CLICK' : 'MY_FILTER_CLICK',
      });
      dispatchCompanies({ type: CompanyContextActionTypes.ResetPagination });
      dispatch({
        type: FinancialDataContextActionTypes.Set,
        payload: searchRequest,
      });
    }
  };

  const refreshTable = useCallback(() => refreshTableFn(tableGridApi, dispatchTableApi), [
    tableGridApi,
    dispatchTableApi,
  ]);

  useEffect(() => {
    if (refresh) {
      refreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  return (
    <Box className={classNames([classes.root, className])}>
      <TableGrid
        columns={columns}
        getDatasource={getDatasource(datasource)}
        isShowSideBar={false}
        suppressTableContextProvider={true}
        originalProps={{
          context: {
            pagination,
            setPagination,
            dispatch,
            isPredefinedFilters,
          },
          headerHeight: 0,
          rowModelType: 'serverSide',
          pagination: true,
          paginationPageSize: pagination.size,
          cacheBlockSize: pagination.size,
          maxBlocksInCache: 1,
          suppressPaginationPanel: true,
          rowSelection: 'single',
          rowMultiSelectWithClick: false,
          onRowClicked: onRowClicked,
        }}
      >
        {pagination.totalPages > 1 ? (
          <TablePagination
            gridApi={tableGridApi}
            pagination={pagination}
            onChange={(value: number) => {
              setPagination({
                ...pagination,
                page: value,
              });
            }}
          />
        ) : null}
      </TableGrid>
    </Box>
  );
};

export function getDatasource(datasource: any) {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      const model: any = params.api.getModel();
      const {
        pagination,
        setPagination,
        dispatch,
        isPredefinedFilters,
      } = model.getContext().getBean('gridOptions').context;
      let totalPages = 0;
      let totalElements = 0;

      const requestParams: RequestParams = {
        page: pagination.page > 0 ? pagination.page - 1 : 0,
        size: pagination.size,
      };

      try {
        const response: PageOfQueryDto = await datasource(requestParams);

        if (response) {
          const result = response?.content ?? [];

          if (result.length <= 0) {
            params.api.showNoRowsOverlay();
            params.successCallback([], 0);
          } else {
            params.api.hideOverlay();
            totalElements = response?.totalElements!;
            totalPages = response?.totalPages!;
            params.successCallback(result, totalElements);
            if (isPredefinedFilters) {
              dispatch({
                type: FinancialDataContextActionTypes.SetPredefinedFilters,
                payload: result,
              });
            }
          }
        } else {
          params.failCallback();
        }
      } catch {
        params.api.showNoRowsOverlay();
        params.successCallback([], 0);
      } finally {
        setPagination({
          ...pagination,
          totalPages,
          totalElements,
        });
      }
    },
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    height: 'calc(100vh - 175px)',
    '& .ag-theme-alpine': {
      borderLeft: 0,
      borderRight: 0,
      '& .ag-layout-auto-height': {
        padding: 0,
      },
    },
    '& .ag-row-selected': {
      backgroundColor: theme.palette.primary.main,

      '& .companyTitle': {
        '& > div > div': {
          color: theme.palette.background.paper,
        },
      },
    },
  },
  header: {
    padding: theme.spacing(6),
    fontSize: FontSizes.size.headerSize,
    fontWeight: FontSizes.fontWeight.bold,
    color: theme.palette.text.secondary,
  },
}));
